import { patchOrgProxyConfigsByProxyConfigIdMutation } from '@onefootprint/fetch-dashboard';
import { getErrorMessage } from '@onefootprint/request/src/request';
import type { ProxyConfigBasic } from '@onefootprint/request-types/dashboard';
import { useToast } from '@onefootprint/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ConfirmationDialog from '@/components/confirmation-dialog';
export type StatusHandler = {
  toggle: () => void;
};
export type StatusProps = {
  proxyConfig: ProxyConfigBasic;
};
const Status = forwardRef<StatusHandler, StatusProps>(({
  proxyConfig
}, ref) => {
  const {
    t
  } = useTranslation('vault-proxy', {
    keyPrefix: 'actions.status'
  });
  const {
    t: notificationsT
  } = useTranslation('vault-proxy', {
    keyPrefix: 'notifications.edit'
  });
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const mutation = useMutation(patchOrgProxyConfigsByProxyConfigIdMutation());
  const showConfirmation = () => {
    setOpen(true);
  };
  const hideConfirmation = () => {
    setOpen(false);
  };
  const toggleStatus = () => {
    mutation.mutate({
      path: {
        proxyConfigId: proxyConfig.id
      },
      body: {
        status: proxyConfig.status === 'enabled' ? 'disabled' : 'enabled'
      }
    }, {
      onSuccess: () => {
        toast({
          description: notificationsT($ => $.success.description, {
            name: proxyConfig.name
          }),
          title: notificationsT($ => $.success.title)
        });
        queryClient.invalidateQueries();
        hideConfirmation();
      },
      onError: (error: unknown) => {
        toast({
          description: getErrorMessage(error),
          title: notificationsT($ => $.error.title),
          variant: 'error'
        });
      }
    });
  };
  const handleToggle = () => {
    showConfirmation();
  };
  useImperativeHandle(ref, () => ({
    toggle: handleToggle
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  return <ConfirmationDialog isPending={mutation.isPending} onClose={hideConfirmation} onConfirm={toggleStatus} open={open} title={proxyConfig.status === 'enabled' ? t($ => $.disable.confirmation.title) : t($ => $.enable.confirmation.title)}>
      <Trans ns="vault-proxy" i18nKey={proxyConfig.status === 'enabled' ? $ => $.actions.status.disable.confirmation.description : $ => $.actions.status.enable.confirmation.description} components={{
      b: <b className="font-semibold text-label-2" />
    }} values={{
      name: proxyConfig.name
    }} />
    </ConfirmationDialog>;
});
export default Status;